// Seite „Geplante Nachrichten" (/scheduled). Vertrag: docs/API.md 3.9.
import type { ChannelDto, ScheduledMessageDto, ScheduledStatus } from '@lagerfeuer/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { messageLink, scheduledApi, useMaxMessageLength } from '../api';
import { formatDateTime, formatRelative, fromLocalInput, toLocalInput } from '../format';
import { toast } from '../toast';
import { Badge, Button, ConfirmButton, Empty, ErrorBox, ExtrasPage, Field, Loading, Tabs } from '../ui';
import { channelLabel, useChannelLookup } from './channels';
import { SCHEDULED_KEY, scheduledListKey, sortScheduled, type ScheduledFilter } from './realtime';
import { dayKey, dayLabel, useNow, validateSendAt } from './time';
import './scheduled.css';

type FilterTab = 'pending' | 'sent' | 'failed' | 'canceled' | 'all';
const TAB_TO_FILTER: Record<FilterTab, ScheduledFilter> = { pending: 'PENDING', sent: 'SENT', failed: 'FAILED', canceled: 'CANCELED', all: 'all' };
const TABS: { value: FilterTab; label: string }[] = [
  { value: 'pending', label: 'Ausstehend' },
  { value: 'sent', label: 'Gesendet' },
  { value: 'failed', label: 'Fehlgeschlagen' },
  { value: 'canceled', label: 'Abgebrochen' },
  { value: 'all', label: 'Alle' },
];

const STATUS_LABEL: Record<ScheduledStatus, { label: string; tone: 'accent' | 'ok' | 'danger' | 'neutral' }> = {
  PENDING: { label: 'Ausstehend', tone: 'accent' },
  SENT: { label: 'Gesendet', tone: 'ok' },
  FAILED: { label: 'Fehlgeschlagen', tone: 'danger' },
  CANCELED: { label: 'Abgebrochen', tone: 'neutral' },
};

export function ScheduledPage() {
  const [tab, setTab] = useState<FilterTab>('pending');
  const filter = TAB_TO_FILTER[tab];
  const now = useNow();
  const list = useQuery({
    queryKey: scheduledListKey(filter),
    queryFn: () => scheduledApi.list(filter === 'all' ? {} : { status: filter }),
    select: (items) => sortScheduled(items, filter),
  });
  const items = list.data ?? [];
  const channels = useChannelLookup(items.map((s) => s.channelId));

  const groups: { key: string; label: string; items: ScheduledMessageDto[] }[] = [];
  for (const s of items) {
    const key = dayKey(s.sendAt);
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.items.push(s);
    else groups.push({ key, label: dayLabel(s.sendAt, now), items: [s] });
  }

  return (
    <ExtrasPage title="Geplante Nachrichten" testId="scheduled-page">
      <Tabs value={tab} onChange={setTab} items={TABS} testId="scheduled-filter" />
      {list.isPending ? (
        <Loading testId="scheduled-loading" />
      ) : list.isError ? (
        <ErrorBox error={list.error} testId="scheduled-error" />
      ) : items.length === 0 ? (
        <Empty testId="scheduled-empty">
          {tab === 'pending' ? 'Keine ausstehenden Nachrichten. Plane eine über die Kanal-Kopfzeile („Nachricht planen").' : 'Keine Einträge.'}
        </Empty>
      ) : (
        <div className="lfx-stack" data-testid="scheduled-list">
          {groups.map((g) => (
            <section key={g.key} className="lfx-sched-group" aria-label={g.label} data-testid="scheduled-group">
              <h2 className="lfx-sched-group__title">{g.label}</h2>
              <ul className="lfx-sched-list">
                {g.items.map((s) => (
                  <ScheduledItem key={s.id} item={s} channel={channels.get(s.channelId)} now={now} />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </ExtrasPage>
  );
}

function ScheduledItem({ item, channel, now }: { item: ScheduledMessageDto; channel: ChannelDto | null | undefined; now: number }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const status = STATUS_LABEL[item.status];
  const cancel = useMutation({
    mutationFn: () => scheduledApi.cancel(item.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SCHEDULED_KEY });
      toast({ kind: 'info', title: 'Geplante Nachricht abgebrochen' });
    },
  });
  return (
    <li className={`lfx-card lfx-sched-item lfx-sched-item--${item.status.toLowerCase()}`} data-testid="scheduled-item" data-id={item.id} data-status={item.status}>
      <div className="lfx-row lfx-row--between">
        <div className="lfx-row">
          <Badge tone={status.tone} testId="scheduled-item-status">
            {status.label}
          </Badge>
          <span className="lfx-sched-channel" data-testid="scheduled-item-channel">
            {channelLabel(channel)}
          </span>
        </div>
        <time className="lfx-muted" dateTime={item.sendAt} data-testid="scheduled-item-time" title={formatDateTime(item.sendAt)}>
          {formatDateTime(item.sendAt)} · {formatRelative(item.sendAt, now)}
        </time>
      </div>
      {editing ? (
        <ScheduledEditForm item={item} onDone={() => setEditing(false)} />
      ) : (
        <div className="lfx-sched-content" data-testid="scheduled-item-content">
          {item.content}
        </div>
      )}
      {item.status === 'FAILED' && (
        <div className="lfx-error" data-testid="scheduled-item-failure">
          Senden fehlgeschlagen{item.error ? `: ${item.error}` : '.'}
        </div>
      )}
      <ErrorBox error={cancel.error} testId="scheduled-item-error" />
      {!editing && (
        <div className="lfx-row">
          {item.status === 'SENT' && item.sentMessageId && channel && (
            <Link className="lfx-link" to={messageLink(channel.serverId, item.channelId, item.sentMessageId)} data-testid="scheduled-item-jump">
              Zur gesendeten Nachricht
            </Link>
          )}
          {item.status === 'PENDING' && (
            <>
              <Button testId="scheduled-item-edit" onClick={() => setEditing(true)}>
                Bearbeiten
              </Button>
              <ConfirmButton label="Abbrechen" confirmLabel="Wirklich abbrechen?" testId="scheduled-item-cancel" disabled={cancel.isPending} onConfirm={() => cancel.mutate()} />
            </>
          )}
        </div>
      )}
    </li>
  );
}

function ScheduledEditForm({ item, onDone }: { item: ScheduledMessageDto; onDone: () => void }) {
  const qc = useQueryClient();
  const maxLength = useMaxMessageLength();
  const [content, setContent] = useState(item.content);
  const [when, setWhen] = useState(() => toLocalInput(item.sendAt));
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (body: { content?: string; sendAt?: string }) => scheduledApi.update(item.id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SCHEDULED_KEY });
      toast({ kind: 'success', title: 'Geplante Nachricht gespeichert' });
      onDone();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const body: { content?: string; sendAt?: string } = {};
    if (content !== item.content) {
      if (!content.trim()) return setError('Bitte einen Text eingeben.');
      if (content.length > maxLength) return setError(`Höchstens ${maxLength} Zeichen.`);
      body.content = content;
    }
    if (when !== toLocalInput(item.sendAt)) {
      const sendAt = fromLocalInput(when);
      const err = validateSendAt(sendAt);
      if (err || !sendAt) return setError(err);
      body.sendAt = sendAt;
    }
    setError(null);
    if (!body.content && !body.sendAt) return onDone();
    save.mutate(body);
  };
  return (
    <form className="lfx-stack" onSubmit={submit} data-testid="scheduled-edit-form" noValidate>
      <Field label="Nachricht" hint={`${content.length} / ${maxLength}`}>
        {(id) => <textarea id={id} className="lfx-textarea" data-testid="scheduled-edit-content" value={content} onChange={(e) => setContent(e.target.value)} />}
      </Field>
      <Field label="Zeitpunkt">
        {(id) => <input id={id} type="datetime-local" className="lfx-input" data-testid="scheduled-edit-when" value={when} onChange={(e) => setWhen(e.target.value)} />}
      </Field>
      {error && (
        <div className="lfx-field__error" role="alert" data-testid="scheduled-edit-validation">
          {error}
        </div>
      )}
      <ErrorBox error={save.error} testId="scheduled-edit-error" />
      <div className="lfx-row">
        <Button variant="primary" type="submit" testId="scheduled-edit-save" disabled={save.isPending}>
          Speichern
        </Button>
        <Button testId="scheduled-edit-abort" onClick={onDone}>
          Verwerfen
        </Button>
      </div>
    </form>
  );
}
